// frontend/src/hooks/useChangeEffect.js
//
// Run an effect when `value` actually CHANGES between renders — not on the
// initial mount, and not when the effect is merely re-created.
//
// A plain `useEffect(fn, [value])` fires on mount and again whenever React
// re-creates the effect, and a dependency-array effect cannot tell "the value
// changed" apart from "the effect ran again". This hook keys off the VALUE,
// not the run count: it compares against the last value it acted on, held
// in a ref, so a re-run with an unchanged value does nothing while a genuine
// change fires on the next commit.
//
// Contract:
//   - Does NOT fire on mount (the first value is the baseline).
//   - Fires once per genuine change of `value` (compared with Object.is).
//   - Always runs the current `effect` (it is read from the firing render's
//     closure — re-runs caused only by a new callback identity no-op,
//     because the value is unchanged).
//   - No cleanup phase — `effect` is a fire-and-forget reaction. Use a
//     plain useEffect if you need symmetric setup/teardown.

import { useEffect, useRef } from 'react';

export function useChangeEffect(value, effect) {
  const lastValueRef = useRef(value);

  // `effect` is in the deps so the latest callback is always used and
  // exhaustive-deps stays satisfied. The value comparison — not the deps
  // array — decides whether the body fires: a re-run with an unchanged
  // value (a new inline callback) simply no-ops.
  useEffect(() => {
    if (!Object.is(lastValueRef.current, value)) {
      lastValueRef.current = value;
      effect();
    }
  }, [value, effect]);
}
